import React from 'react';
import {
    Bell,
    CalendarClock,
    Calculator,
    CreditCard,
    FileBarChart,
    FileCheck,
    FileSpreadsheet,
    FileText,
    Filter,
    GripVertical,
    LayoutDashboard,
    Link2,
    MessageSquareText,
    Navigation,
    ShieldAlert,
    ShieldCheck,
    UserCircle,
    Wand2,
} from 'lucide-react';
import { EXTERNAL_WORKY_CALENDLY_DEMO_BOOKING_URL } from '../../analytics/openWorkyCalendlyDemo';

const bookDemoLink: string = EXTERNAL_WORKY_CALENDLY_DEMO_BOOKING_URL;

enum OperationsProductKey {
    INVOICES = 'invoices',
    SCHEDULE = 'schedule',
    WORKFORCE = 'workforce',
    CLOCK = 'clock',
    PAY = 'pay',
    ASSISTANT = 'assistant',
    TRUST_VAULT = 'trust-vault',
}

interface OperationsProductFeature {
    readonly icon: React.ReactNode;
    readonly title: string;
    readonly description: string;
}

interface OperationsProductItem {
    readonly key: OperationsProductKey;
    readonly title: string;
    readonly subtitle: string;
    readonly description: string;
    readonly photos: readonly string[];
    readonly photoChips: readonly string[];
    readonly features: readonly OperationsProductFeature[];
    readonly buttonLabel: string;
    readonly buttonLink: string;
}

const OPERATIONS_PRODUCTS: readonly OperationsProductItem[] = [
    {
        key: OperationsProductKey.INVOICES,
        title: 'Invoices',
        subtitle: 'Custom client invoice PDFs, generated from your exact client business contract rules',
        description:
            'Create, send, and track invoices in seconds. Worky Invoices automates your billing workflow so you get paid faster — with real-time status tracking, automated reminders, and seamless integration with your existing accounting tools.',
        photos: ['/product-invoices-1.png', '/product-invoices-2.png', '/product-invoices-3.png'],
        photoChips: ['Create Invoice', 'Track Status', 'Send & Remind'],
        features: [
            { icon: <FileText size={30} />, title: 'Auto-Generate', description: 'Create invoices from completed shifts automatically' },
            { icon: <CreditCard size={30} />, title: 'Track Payments', description: 'Real-time status updates on every invoice sent' },
            { icon: <Link2 size={30} />, title: 'Integrations', description: 'Syncs with QuickBooks, Xero, and more' },
        ],
        buttonLabel: 'Start Invoicing',
        buttonLink: bookDemoLink,
    },
    {
        key: OperationsProductKey.SCHEDULE,
        title: 'Smart Match',
        subtitle: 'AI-driven placement that never sleeps',
        description:
            'Intelligently match workers to open shifts based on skills, availability, location, and preferences. Smart Match eliminates manual scheduling and fills positions faster with the right people every time.',
        photos: ['/product-schedule-1.png', '/product-schedule-2.png', '/product-schedule-3.png'],
        photoChips: ['Match Workers', 'Resolve Conflicts', 'Build Schedule'],
        features: [
            { icon: <Wand2 size={30} />, title: 'Auto-Fill', description: 'AI-powered shift matching based on worker profiles' },
            { icon: <ShieldAlert size={30} />, title: 'Conflict Detection', description: 'Prevents double-booking and overtime violations' },
            { icon: <GripVertical size={30} />, title: 'Drag & Drop', description: 'Visual schedule builder for quick manual edits' },
        ],
        buttonLabel: 'Start Scheduling',
        buttonLink: bookDemoLink,
    },
    {
        key: OperationsProductKey.WORKFORCE,
        title: 'Workforce',
        subtitle: 'Manage your workforce',
        description:
            'Maintain a centralized hub for every worker — from onboarding documents and certifications to performance history. Workforce gives you full visibility into your talent pool at a glance.',
        photos: ['/product-workforce-1.png', '/product-workforce-2.png', '/product-workforce-3.png'],
        photoChips: ['Worker Profiles', 'Certifications', 'Search & Filter'],
        features: [
            { icon: <UserCircle size={30} />, title: 'Worker Profiles', description: 'Complete profiles with skills, docs, and history' },
            { icon: <ShieldCheck size={30} />, title: 'Compliance', description: 'Track certifications and expiry dates automatically' },
            { icon: <Filter size={30} />, title: 'Search & Filter', description: 'Find the right worker instantly with smart filters' },
        ],
        buttonLabel: 'Manage Workers',
        buttonLink: bookDemoLink,
    },
    {
        key: OperationsProductKey.CLOCK,
        title: 'Time Clock',
        subtitle: 'Track time and attendance',
        description:
            'Clock-event location verification for your workforce. Location is collected only at clock-in and clock-out—not continuously. Workers are notified and can opt out. Used for scheduling and client disputes; retained for 1 year. Exportable timesheets feed payroll and billing.',
        photos: ['/product-clock-1.png', '/product-clock-2.png', '/product-clock-3.png'],
        photoChips: ['GPS Check-In', 'Live Dashboard', 'Export Timesheets'],
        features: [
            { icon: <Navigation size={30} />, title: 'GPS at Clock Events', description: 'Confirm on-site presence when workers clock in or out—not continuous tracking' },
            { icon: <LayoutDashboard size={30} />, title: 'Live Dashboard', description: 'See who is clocked in across all locations' },
            { icon: <FileSpreadsheet size={30} />, title: 'Timesheet Export', description: 'One-click export for payroll processing' },
        ],
        buttonLabel: 'Start Tracking',
        buttonLink: bookDemoLink,
    },
    {
        key: OperationsProductKey.PAY,
        title: 'Worky Pay',
        subtitle: 'Flexible payroll for your contractors and employees',
        description:
            'Run payroll in minutes, not hours. Worky Pay calculates hours, applies rates, handles deductions, and sends payments — all in one streamlined flow connected to your time tracking data.',
        photos: ['/product-pay-1.png', '/product-pay-2.png', '/product-pay-3.png'],
        photoChips: ['Calculate Hours', 'Run Payroll', 'Pay Stubs', 'Generate Reports'],
        features: [
            { icon: <Calculator size={30} />, title: 'Auto-Calculate', description: 'Hours and rates are automatically calculated from timesheet data' },
            { icon: <CalendarClock size={30} />, title: 'Flexible Pay', description: 'Support for weekly, bi-weekly, and on-demand pay' },
            { icon: <FileCheck size={30} />, title: 'Digital Pay Stubs', description: 'Automatic digital pay stubs for every worker' },
        ],
        buttonLabel: 'Start Paying',
        buttonLink: bookDemoLink,
    },
    {
        key: OperationsProductKey.ASSISTANT,
        title: 'Assistant',
        subtitle: 'Your personal assistant',
        description:
            'An AI-powered assistant that helps you manage your day-to-day operations — from answering worker questions to generating reports and flagging issues before they become problems.',
        photos: ['/product-assistant-1.png', '/product-assistant-2.png', '/product-assistant-3.png'],
        photoChips: ['Smart Alerts', 'Ask Anything', 'Generate Reports'],
        features: [
            { icon: <Bell size={30} />, title: 'Smart Alerts', description: 'Proactive notifications for schedule gaps and compliance' },
            { icon: <MessageSquareText size={30} />, title: 'Natural Language', description: 'Ask questions and get answers in plain English' },
            { icon: <FileBarChart size={30} />, title: 'Report Builder', description: 'Generate custom reports with a single request' },
        ],
        buttonLabel: 'Coming Soon',
        buttonLink: bookDemoLink,
    },
    {
        key: OperationsProductKey.TRUST_VAULT,
        title: 'Trust Vault',
        subtitle: 'Secure credential and document management',
        description:
            'Store, verify, and manage worker credentials, licenses, and compliance documents in one secure vault. Trust Vault keeps your agency audit-ready with automated expiry tracking and instant access when you need it.',
        photos: ['/product-trust-vault-1.png', '/product-trust-vault-2.png', '/product-trust-vault-3.png'],
        photoChips: ['Document Storage', 'Expiry Alerts', 'Audit Trail'],
        features: [
            { icon: <ShieldCheck size={30} />, title: 'Secure Storage', description: 'Encrypted document vault for licenses and certifications' },
            { icon: <FileCheck size={30} />, title: 'Verification', description: 'Track credential status and approval workflows' },
            { icon: <ShieldAlert size={30} />, title: 'Expiry Alerts', description: 'Automated reminders before documents expire' },
        ],
        buttonLabel: 'Explore Trust Vault',
        buttonLink: bookDemoLink,
    },
];

const getOperationsProductByKey = (key: OperationsProductKey): OperationsProductItem | undefined =>
    OPERATIONS_PRODUCTS.find((p) => p.key === key);

export type { OperationsProductFeature, OperationsProductItem };
export { OperationsProductKey, OPERATIONS_PRODUCTS, getOperationsProductByKey };
